import React, { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import CloseButton from "components/CloseButton";
import { FilterDate } from "components/Filters/FilterDate";
import { IconType } from "enums/Icon";
import { FormikProps } from "formik";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import {
  ButtonsWrapper,
  FilterIcon,
  FiltersWrapper,
  MenuIcon,
  SearchFilterButton,
  SearchWrapper,
} from "styles/searchFilterStyles";
import { StorageSearchValues } from "types/StorageForms/Search";
import SearchInput from "../../../../components/SearchInput/SearchInput";
import useContainerQuery from "../../../../hooks/useContainerQuery";
import FilterShop from "./Filter/FilterShop";
import FilterWeight from "./Filter/FilterWeight";
import SearchModeToggle from "./SearchModeToggle";

interface SearchProps {
  formOptions: FormikProps<StorageSearchValues>;
}

// Search-row width (px) below which the filter chips no longer fit next to
// the mode toggle and the input, and get condensed into the "Tools" button.
// The row narrows with both the viewport and the right-panel squeeze.
const FILTERS_MIN_WIDTH = 720;

const Search: FC<SearchProps> = React.memo(({ formOptions }) => {
  const { t } = useTranslation("common");
  const {
    values,
    initialValues,
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
    dirty,
  } = formOptions;

  const [isOpen, setOpened] = useState(true);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isCompact = useContainerQuery<boolean | null>(
    wrapperRef,
    (width) => width < FILTERS_MIN_WIDTH,
    null,
  );

  // Auto-collapse the chips into "Tools" when the row gets too narrow and
  // reopen them when it widens again. Only threshold crossings set the
  // state, so the manual toggle keeps working in between.
  useLayoutEffect(() => {
    if (isCompact !== null) setOpened(!isCompact);
  }, [isCompact]);

  const filterProps = useMemo(
    () => ({
      values,
      initialValues,
      handleSubmit,
      handleChange,
      setFieldValue,
    }),
    [values, initialValues, handleSubmit, handleChange, setFieldValue],
  );

  const toggleFilterMenu = async () => {
    if (isOpen && dirty) {
      setValues({
        ...initialValues,
        trackingNumber: values.trackingNumber,
        searchMode: values.searchMode,
      });
      await Promise.resolve();
      handleSubmit();
    }
    setOpened(!isOpen);
  };

  return (
    <>
      {/* Panel-open squeeze is handled at the page level by Storage's Wrapper,
            so no $isPanelOpened here, unlike the sibling Search components. */}
      <SearchWrapper ref={wrapperRef} $isOpen={isOpen}>
        <SearchModeToggle {...formOptions} />
        <SearchInput<StorageSearchValues>
          name="trackingNumber"
          value={values.trackingNumber}
          initialValue={initialValues.trackingNumber}
          placeholder={t("parcels.search.filterByTransactionNumber")}
          {...filterProps}
        />
        <ButtonsWrapper $isOpen={isOpen}>
          {isOpen && (
            <FiltersWrapper>
              <FilterWeight {...formOptions} />
              <FilterDate {...formOptions} />
              <FilterShop {...formOptions} />
            </FiltersWrapper>
          )}
          {isOpen ? (
            <CloseButton compact onClick={toggleFilterMenu} />
          ) : (
            <SearchFilterButton onClick={toggleFilterMenu}>
              <Hidden sm>{t("parcels.search.tools")}</Hidden>
              <Visible sm>
                <FilterIcon type={IconType.Filter} />
              </Visible>
              <MenuIcon type={IconType.Menu} />
            </SearchFilterButton>
          )}
        </ButtonsWrapper>
      </SearchWrapper>
    </>
  );
});

export default Search;
